import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type AuditLogFilterField = "action" | "admin" | "target";
export type AuditLogFilterOperator = "contains" | "equals";

export interface AuditLogFilterTag {
  id: string;
  field: AuditLogFilterField;
  value: string;
  label: string;
  operator?: AuditLogFilterOperator;
}

export interface AuditLog {
  id: string;
  admin_id: string;
  target_user_id: string;
  action: string;
  details: Record<string, unknown> | null;
  created_at: string;
  admin_name: string | null;
  admin_email: string | null;
  target_name: string | null;
  target_email: string | null;
}

export type AuditLogSortField = "created_at" | "action" | "admin" | "target";
export type SortDirection = "asc" | "desc";

const LOGS_PER_PAGE = 10;
const AUDIT_SORT_STORAGE_KEY = "admin-audit-sort";

const getInitialAuditSort = (): { field: AuditLogSortField; direction: SortDirection } => {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem(AUDIT_SORT_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall through
      }
    }
  }
  return { field: "created_at", direction: "desc" };
};

// Helper to compare strings with operators
const compareStringWithOperator = (value: string, filterValue: string, operator: AuditLogFilterOperator): boolean => {
  switch (operator) {
    case "equals": return value.toLowerCase() === filterValue.toLowerCase();
    case "contains": return value.toLowerCase().includes(filterValue.toLowerCase());
    default: return value.toLowerCase().includes(filterValue.toLowerCase());
  }
};

export function useAdminAuditLogs() {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterTags, setFilterTags] = useState<AuditLogFilterTag[]>([]);
  
  const initialSort = getInitialAuditSort();
  const [sortField, setSortField] = useState<AuditLogSortField>(initialSort.field);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSort.direction);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-audit-logs"],
    queryFn: async () => {
      // Fetch all audit logs
      const { data: logs, error: logsError } = await supabase
        .from("admin_audit_logs")
        .select("*")
        .order("created_at", { ascending: false });

      if (logsError) throw logsError;
      if (!logs || logs.length === 0) return [];

      // Get unique user IDs for profile lookups
      const adminIds = [...new Set(logs.map((log) => log.admin_id))];
      const targetIds = [...new Set(logs.map((log) => log.target_user_id))];
      const allIds = [...new Set([...adminIds, ...targetIds])];

      // Fetch profiles
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", allIds);

      const profileMap = new Map(
        (profiles || []).map((p) => [
          p.id,
          {
            name: `${p.first_name || ""} ${p.last_name || ""}`.trim() || null,
            email: p.email,
          },
        ])
      );

      // Enrich logs with profile data
      return logs.map((log) => ({
        id: log.id,
        admin_id: log.admin_id,
        target_user_id: log.target_user_id,
        action: log.action,
        details: log.details as Record<string, unknown> | null,
        created_at: log.created_at,
        admin_name: profileMap.get(log.admin_id)?.name || null,
        admin_email: profileMap.get(log.admin_id)?.email || null,
        target_name: profileMap.get(log.target_user_id)?.name || null,
        target_email: profileMap.get(log.target_user_id)?.email || null,
      }));
    },
  });

  const logs = data || [];

  // Filter logic
  const filteredLogs = useMemo(() => {
    if (filterTags.length === 0) return logs;

    return logs.filter((log) => {
      // Group tags by field
      const actionFilters = filterTags.filter((t) => t.field === "action");
      const adminFilters = filterTags.filter((t) => t.field === "admin");
      const targetFilters = filterTags.filter((t) => t.field === "target");

      // OR within same field, AND across fields
      const matchesAction =
        actionFilters.length === 0 ||
        actionFilters.some((t) => log.action.toLowerCase() === t.value.toLowerCase());

      const matchesAdmin =
        adminFilters.length === 0 ||
        adminFilters.some((t) => {
          const adminStr = `${log.admin_name || ""} ${log.admin_email || ""}`;
          return compareStringWithOperator(adminStr, t.value, t.operator || "contains");
        });

      const matchesTarget =
        targetFilters.length === 0 ||
        targetFilters.some((t) => {
          const targetStr = `${log.target_name || ""} ${log.target_email || ""}`;
          return compareStringWithOperator(targetStr, t.value, t.operator || "contains");
        });

      return matchesAction && matchesAdmin && matchesTarget;
    });
  }, [logs, filterTags]);

  // Sort logic
  const sortedLogs = useMemo(() => {
    const sorted = [...filteredLogs];
    sorted.sort((a, b) => {
      let aVal: string | number = "";
      let bVal: string | number = "";

      switch (sortField) {
        case "created_at":
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case "action":
          aVal = a.action.toLowerCase();
          bVal = b.action.toLowerCase();
          break;
        case "admin":
          aVal = (a.admin_name || a.admin_email || "").toLowerCase();
          bVal = (b.admin_name || b.admin_email || "").toLowerCase();
          break;
        case "target":
          aVal = (a.target_name || a.target_email || "").toLowerCase();
          bVal = (b.target_name || b.target_email || "").toLowerCase();
          break;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredLogs, sortField, sortDirection]);

  // Pagination
  const totalCount = sortedLogs.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / LOGS_PER_PAGE));
  const paginatedLogs = sortedLogs.slice(
    (currentPage - 1) * LOGS_PER_PAGE,
    currentPage * LOGS_PER_PAGE
  );

  // Filter actions
  const addFilter = useCallback((field: AuditLogFilterField, value: string, operator?: AuditLogFilterOperator) => {
    const label =
      field === "action" ? "Action" : field === "admin" ? "Admin" : "Target";
    const newTag: AuditLogFilterTag = {
      id: `${field}-${value}-${operator || "default"}-${Date.now()}`,
      field,
      value,
      label,
      operator,
    };
    setFilterTags((prev) => {
      // Prevent duplicates
      if (prev.some((t) => t.field === field && t.value.toLowerCase() === value.toLowerCase() && t.operator === operator)) {
        return prev;
      }
      return [...prev, newTag];
    });
    setCurrentPage(1);
  }, []);

  const removeFilter = useCallback((tagId: string) => {
    setFilterTags((prev) => prev.filter((t) => t.id !== tagId));
    setCurrentPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilterTags([]);
    setCurrentPage(1);
  }, []);

  // Sort actions
  const handleSort = useCallback((field: AuditLogSortField) => {
    let newDirection: SortDirection;
    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
      newDirection = "desc";
    }
    setSortField(field);
    setSortDirection(newDirection);
    localStorage.setItem(AUDIT_SORT_STORAGE_KEY, JSON.stringify({ field, direction: newDirection }));
    setCurrentPage(1);
  }, [sortField, sortDirection]);

  return {
    logs: paginatedLogs,
    totalCount,
    isLoading,
    error,
    currentPage,
    setCurrentPage,
    totalPages,
    logsPerPage: LOGS_PER_PAGE,
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,
    sortField,
    sortDirection,
    handleSort,
  };
}
